import { useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { secondaryText } from '../../../../../styling/stylingHelper';
import {
  reprocessAICheckerEmail,
  reauditAICheckerEmail,
  getAICheckerEmailById,
  deepCheckAICheckerEmail,
  deleteAICheckerOrder,
  deleteAICheckerRevenue,
  type AICheckerTrace,
} from '../../../../../services/admin/adminService';
import { Bug, Check, ListChecks, RotateCw, Trash2 } from 'lucide-react';
import { deepCheckOutcome } from './deepCheckOutcome';
import { buildAICheckerEmailPrompt } from './emailPrompt';
import type { TFn } from '../types';

export function EmailIssuePromptCopyCell({ trace, t }: { trace: AICheckerTrace; t: TFn }) {
  const [copied, setCopied] = useState(false);
  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(buildAICheckerEmailPrompt(trace));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy AI checker prompt', err);
    }
  };
  return (
    <button
      onClick={handleClick}
      className={cn('px-2 py-1 rounded-md text-xs transition-colors', copied
        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
        : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/50')}
    >
      {copied ? '✓ ' + t('adminTesting.review.copyIssuePrompt') : t('adminTesting.review.copyIssuePrompt')}
    </button>
  );
}

export function ReprocessButton({ trace, t, onChanged }: { trace: AICheckerTrace; t: TFn; onChanged?: () => void }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState<string>('');

  // Reprocessing searches by rfc822 Message-ID in a specific mailbox.
  const canReprocess = !!trace.rfc822MessageId && (trace.account === 'cloud63' || trace.account === 'cloud64');
  if (!canReprocess) return null;

  const handleClick = async () => {
    if (state === 'loading') return;
    setState('loading');
    try {
      const r = await reprocessAICheckerEmail(trace.account, trace.rfc822MessageId as string);
      setState('done');
      setMsg(t('adminTesting.review.reprocessDone', { found: r.found, processed: r.processed }));
      onChanged?.();
    } catch (err: any) {
      setState('error');
      setMsg(err.message || 'Failed');
    }
  };

  // Success/error are shown as a compact icon (message in the tooltip) so the row width never grows.
  const title = state === 'done' || state === 'error' ? msg : t('adminTesting.review.reprocess');
  return (
    <button
      onClick={handleClick}
      disabled={state === 'loading'}
      title={title}
      className={cn('p-1.5 rounded-md transition-colors disabled:opacity-50',
        state === 'done'
          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
          : state === 'error'
          ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
          : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50')}
    >
      {state === 'done'
        ? <Check className="w-3.5 h-3.5" />
        : <RotateCw className={cn('w-3.5 h-3.5', state === 'loading' && 'animate-spin')} />}
    </button>
  );
}

// Re-run ONLY the audit (no reprocessing) — overwrites the audit row with a fresh pass against the
// currently stored data. Use after a manual fix to the order/revenue.
export function ReauditButton({ trace, t, onChanged }: { trace: AICheckerTrace; t: TFn; onChanged?: () => void }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState<string>('');

  const canReaudit = !!trace.rfc822MessageId && (trace.account === 'cloud63' || trace.account === 'cloud64');
  if (!canReaudit) return null;

  const handleClick = async () => {
    if (state === 'loading') return;
    setState('loading');
    try {
      const r = await reauditAICheckerEmail(trace.account, trace.rfc822MessageId as string);
      setState('done');
      setMsg(t('adminTesting.review.reauditDone', { found: r.found, audited: r.audited }));
      onChanged?.();
    } catch (err: any) {
      setState('error');
      setMsg(err.message || 'Failed');
    }
  };

  // Success/error are shown as a compact icon (message in the tooltip) so the row width never grows.
  const title = state === 'done' || state === 'error' ? msg : t('adminTesting.review.reaudit');
  return (
    <button
      onClick={handleClick}
      disabled={state === 'loading'}
      title={title}
      className={cn('p-1.5 rounded-md transition-colors disabled:opacity-50',
        state === 'done'
          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
          : state === 'error'
          ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
          : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50')}
    >
      {state === 'done'
        ? <Check className="w-3.5 h-3.5" />
        : <Bug className={cn('w-3.5 h-3.5', state === 'loading' && 'animate-spin')} />}
    </button>
  );
}

// Tiny per-row refresh — refetches just this email's audit-log trace (e.g. after an async reprocess)
// and swaps it into the list in place, without reloading the whole feed.
export function RowReloadButton({ trace, onReloaded, t }: { trace: AICheckerTrace; onReloaded: (fresh: AICheckerTrace) => void; t: TFn }) {
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const handleClick = async () => {
    if (state === 'loading') return;
    setState('loading');
    try {
      const fresh = await getAICheckerEmailById(trace.id);
      onReloaded(fresh);
      setState('idle');
    } catch (err) {
      console.error('Failed to reload audit row', err);
      setState('error');
      setTimeout(() => setState('idle'), 2000);
    }
  };
  return (
    <button
      onClick={handleClick}
      disabled={state === 'loading'}
      title={t('adminTesting.review.reloadRow')}
      className={cn('p-1 rounded-md transition-colors disabled:opacity-50',
        state === 'error' ? 'text-red-500 dark:text-textRed-dark' : cn('hover:bg-gray-100 dark:hover:bg-gray-700', secondaryText))}
    >
      <RotateCw className={cn('w-3 h-3', state === 'loading' && 'animate-spin')} />
    </button>
  );
}

// Consolidated-factuur completeness check — extracts every pakbon/order number from the invoice and
// confirms each is stored. Only shown when the email's verdict is "wrong" (the false-positive case).
// `onRun` is passed on the emails table, where the run belongs to the row: the button becomes a pure
// trigger and the outcome shows as a row chip (readable, dismissible, several rows at once). Without it
// — inside the detail modal, which has no row to pin to — it runs and reports on its own.
export function DeepCheckButton({ trace, t, onRun, running }: { trace: AICheckerTrace; t: TFn; onRun?: () => void; running?: boolean }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState<string>('');

  // Show the verzamelfactuur deep check whenever the verify was uncertain — 'wrong' OR 'inconclusive'
  // (multi-order/consolidated invoices often land inconclusive; the deep check is exactly what resolves them).
  // Backprocessing emails (many invoice PDFs) also get the button on ANY verdict — the check confirms
  // every file's orders were stored, which the per-email verify can't judge.
  const isBackprocessing = /backprocessing\s*#/i.test(trace.emailSubject || '');
  const verdictUncertain = trace.emailVerdict === 'wrong' || trace.emailVerdict === 'inconclusive';
  const canCheck = (verdictUncertain || isBackprocessing) && !!trace.rfc822MessageId && (trace.account === 'cloud63' || trace.account === 'cloud64');
  if (!canCheck) return null;

  const loading = onRun ? !!running : state === 'loading';

  const handleClick = async () => {
    if (loading) return;
    if (onRun) { onRun(); return; }
    setState('loading');
    try {
      const r = await deepCheckAICheckerEmail(trace.account, trace.rfc822MessageId as string);
      const o = deepCheckOutcome(r, t);
      setState(o.ok ? 'done' : 'error');
      setMsg(o.msg);
    } catch (err: any) {
      setState('error');
      setMsg(err.message || 'Failed');
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        onClick={handleClick}
        disabled={loading}
        title={t('adminTesting.review.deepCheck')}
        className={cn('p-1.5 rounded-md transition-colors disabled:opacity-50',
          'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 hover:bg-sky-200 dark:hover:bg-sky-900/50')}
      >
        <ListChecks className={cn('w-3.5 h-3.5', loading && 'animate-pulse')} />
      </button>
      {!onRun && state === 'done' && <span className="text-[10px] text-gray-600 dark:text-gray-300">{msg}</span>}
      {!onRun && state === 'error' && <span className="text-[10px] text-red-500 dark:text-textRed-dark">{msg}</span>}
    </span>
  );
}

// Delete the stored order(s) or revenue entries created from this email (super-admin). Destructive;
// re-audit afterwards to refresh the trace. Only shown for emails that actually created an order or
// revenue row.
export function DeleteOrderButton({ trace, t, onChanged }: { trace: AICheckerTrace; t: TFn; onChanged?: () => void }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState<string>('');

  const isRevenue = trace.matchedIn === 'revenue';
  if ((!trace.hasStoredOrderData && trace.matchedIn !== 'order' && !isRevenue) || !trace.gmailMessageId) return null;

  const confirmKey = isRevenue ? 'adminTesting.review.deleteRevenueConfirm' : 'adminTesting.review.deleteOrderConfirm';
  const titleKey = isRevenue ? 'adminTesting.review.deleteRevenue' : 'adminTesting.review.deleteOrder';
  const doneKey = isRevenue ? 'adminTesting.review.deleteRevenueDone' : 'adminTesting.review.deleteOrderDone';

  const handleClick = async () => {
    if (state === 'loading') return;
    if (!window.confirm(t(confirmKey))) return;
    setState('loading');
    try {
      if (isRevenue) {
        const r = await deleteAICheckerRevenue(trace.gmailMessageId);
        setMsg(t(doneKey, { deleted: r.deleted }));
      } else {
        const r = await deleteAICheckerOrder(trace.gmailMessageId);
        setMsg(t(doneKey, { deleted: r.deleted, facturen: r.facturenDeleted }));
      }
      setState('done');
      onChanged?.();
    } catch (err: any) {
      setState('error');
      setMsg(err.message || 'Failed');
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        onClick={handleClick}
        disabled={state === 'loading'}
        title={t(titleKey)}
        className={cn('p-1.5 rounded-md transition-colors disabled:opacity-50',
          'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50')}
      >
        <Trash2 className={cn('w-3.5 h-3.5', state === 'loading' && 'animate-pulse')} />
      </button>
      {state === 'done' && <span className="text-[10px] text-green-600 dark:text-green-400">{msg}</span>}
      {state === 'error' && <span className="text-[10px] text-red-500 dark:text-textRed-dark">{msg}</span>}
    </span>
  );
}
